import React from 'react';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  CardContent,
  Card,
  Box,
} from '@mui/material';
import Page from '../components/Page';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  return (
    <Page title='Privacy Policy'>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          mb: 5,
        }}
      >
        <Grid container justifyContent='center' sx={{ mb: 3 }}>
          <Grid mt={1}>
            <img
              src='/static/images/one_line_logo.png'
              alt='logo'
              style={{
                width: '300px',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />
          </Grid>
        </Grid>

        <Grid container justifyContent='center' sx={{ mb: 3 }}>
          <Grid>
            <Card
              sx={{
                maxWidth: '900px',
                position: 'relative',
                borderRadius: '8px',
                padding: '1px',
                overflow: 'hidden',
              }}
            >
              <CardContent
                sx={{
                  borderRadius: 'inherit',
                  background: '#E0F7FF',
                  p: 4,
                  pb: '12px !important',
                }}
              >
                <Typography
                  fontSize='23px'
                  fontWeight='bold'
                  gutterBottom
                  mt={1}
                >
                  QR Buddy Privacy Policy
                </Typography>

                <Typography fontSize='13px' fontWeight='bold' gutterBottom>
                  Last Updated: October 8, 2025
                </Typography>

                <Typography fontSize='16px' paragraph>
                  KnoCard LLC (“Company,” “we,” “us,” or “our”) respects your
                  privacy. This Privacy Policy explains how we collect, use,
                  disclose, and protect information when you use QR Buddy, a
                  product of KnoCard LLC, through our website, mobile
                  applications, QR code technology, or related services
                  (collectively, the “Services”).
                </Typography>
                <Typography fontSize='16px' paragraph>
                  By accessing or using QR Buddy, you consent to the practices
                  described in this Privacy Policy. If you do not agree, please
                  discontinue use of our Services.
                </Typography>
                <Box mt={4}>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    1. Information We Collect
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    We may collect the following types of information:
                  </Typography>

                  <Typography variant='h6' gutterBottom mt={2}>
                    1.1 From End-Users (Consumers who scan a QR Buddy code):
                  </Typography>
                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> Personal Information:</strong> Name, email,
                            phone number, or other details you choose to provide
                            when you claim a free QR Buddy or request product
                            information.
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong>Usage Information:</strong> QR codes you
                            scan, products or services you show interest in, and
                            your interactions with QR Buddy features (e.g.,
                            saving product details, AI chat responses).
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> Device Information:</strong> Browser type,
                            device type, operating system, and IP address for
                            security and optimization purposes.
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>

                  {/* 1.2 */}
                  <Typography variant='h6' gutterBottom mt={2}>
                    1.2 From Customers :
                  </Typography>
                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> Business Information:</strong> Company
                            name, representative’s name, email, phone, and
                            billing details for account setup and payment
                            processing.
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    2. How We Use Your Information
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    We use collected information to:
                  </Typography>
                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Provide, operate, and maintain QR Buddy.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Deliver requested product details and AI-powered assistance to Users.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Process payments and manage Customer accounts.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Improve and personalize the Services, including AI recommendations.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Communicate updates, promotions, or service-related notices.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Comply with legal obligations and enforce our Terms of Use.' />
                    </ListItem>
                  </List>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    3. How We Share Information
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    We may share your information in the following ways:
                  </Typography>
                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> With Service Providers:</strong> We may
                            share data with third-party vendors (e.g., hosting
                            providers, payment processors, analytics services)
                            who assist in delivering QR Buddy.
                          </Typography>
                        }
                      />
                    </ListItem>

                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> For Legal Purposes:</strong> If required by
                            law, regulation, or legal process, or to protect the
                            safety, rights, or property of KnoCard LLC, our
                            users, or others.
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> In Business Transfers:</strong> In the
                            event of a merger, acquisition, or sale of assets,
                            your information may be transferred as part of the
                            transaction.
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                  <Typography variant='body1' gutterBottom>
                    We will not share/sell your data to any third party for
                    marketing and promotional purposes.
                  </Typography>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    4. Cookies and Tracking Technologies
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    QR Buddy may use cookies, web beacons, and similar
                    technologies to:
                  </Typography>
                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Recognize returning visitors.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Analyze site traffic and usage patterns.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='Improve user experience and personalize content.' />
                    </ListItem>
                  </List>
                  <Typography variant='body1' gutterBottom>
                    You can control cookies through your browser settings, but
                    disabling them may limit site functionality.
                  </Typography>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    5. Data Retention
                  </Typography>

                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> User (Consumer) Data:</strong> Consumer
                            information is retained as long as necessary to
                            deliver it to the purchasing Customer and comply
                            with applicable laws.
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText
                        primary={
                          <Typography component='span'>
                            <strong> Customer (Business) Data:</strong> Business
                            and billing information is retained while your
                            account is active and as required for legal, tax,
                            and accounting purposes.
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    6. Data Security
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    {' '}
                    We implement reasonable technical and organizational
                    safeguards to protect your data against loss, misuse,
                    unauthorized access, disclosure, or alteration. However, no
                    method of transmission over the Internet or storage is 100%
                    secure.
                  </Typography>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    7. Children’s Privacy
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    QR Buddy is not intended for children under 18. We do not
                    knowingly collect information from minors. If we learn that
                    a child under 18 has provided personal information, we will
                    delete it.
                  </Typography>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    8. Your Privacy Rights
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    Depending on your location, you may have rights under
                    privacy laws such as GDPR or CCPA, including:
                  </Typography>
                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='The right to access the information we hold about you.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='The right to request correction or deletion of your data.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='The right to opt out of certain processing activities.' />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                      <ListItemText primary='The right to withdraw consent at any time.' />
                    </ListItem>
                  </List>
                  <Typography variant='body1' gutterBottom>
                    To exercise these rights, contact us at{' '}
                    <Box
                      component={'a'}
                      href='mailto:support@qrbuddy.ai'
                      sx={{
                        textDecoration: 'underline',
                        color: 'primary.main',
                      }}
                    >
                      support@qrbuddy.ai
                    </Box>
                  </Typography>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    9. Third-Party Links and Services
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    QR Buddy may link to third-party websites or services. We
                    are not responsible for the privacy practices of those third
                    parties. Please review their policies before sharing
                    personal information.
                  </Typography>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    10. Changes to this Policy
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    We may update this Privacy Policy from time to time. Changes
                    will be posted here with an updated “Last Updated” date.
                    Continued use of the Services after changes are posted
                    constitutes acceptance of the updated policy.
                  </Typography>
                  <Typography variant='h5' gutterBottom fontWeight='bold'>
                    11. Contact Us
                  </Typography>
                  <Typography fontSize='14px' paragraph>
                    For questions about this Privacy Policy or how your data is
                    handled, please contact us: <br />
                    <br />
                    <strong>KnoCard LLC</strong>
                    <br />
                    <Box
                      component={'a'}
                      href='mailto:support@knocard.com'
                      sx={{
                        textDecoration: 'underline',
                        color: 'primary.main',
                      }}
                    >
                      support@knocard.com
                    </Box>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default PrivacyPolicy;